import axios from "axios";

interface ApiErrorData {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string[]>;
  };
}

/**
 * Check if an error is a "curator profile required" error
 */
export function isCuratorProfileRequiredError(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  const data = err.response?.data as ApiErrorData | undefined;
  return data?.error?.code === "CURATOR_PROFILE_REQUIRED";
}

/**
 * Check if an error indicates access was revoked (403 Forbidden).
 * With checkPermission guards, 403 = no permission, 404 = resource deleted.
 */
export function isAccessRevokedError(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  return err.response?.status === 403;
}

/**
 * Check if an error is a 404 Not Found (resource deleted by another user).
 */
export function isNotFoundError(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  return err.response?.status === 404;
}

/**
 * Extract a user-friendly error message from an API error.
 * Handles validation errors with field-level messages.
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (!axios.isAxiosError(err)) {
    return err instanceof Error ? err.message : fallback;
  }

  const data = err.response?.data as ApiErrorData | undefined;
  if (!data?.error) return fallback;

  // For validation errors, format field errors into a readable message
  if (data.error.code === "VALIDATION_ERROR" && data.error.fields) {
    const fieldErrors = Object.entries(data.error.fields)
      .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
      .join("; ");
    return fieldErrors || data.error.message || fallback;
  }

  return data.error.message || fallback;
}
